import React from "react";
import { Grid, Paper, Typography, Box, Icon } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DescriptionIcon from "@mui/icons-material/Description"; // Example icon, replace with desired icons
import EditIcon from "@mui/icons-material/Edit"; // Replace icons as needed
import ArticleIcon from "@mui/icons-material/Article"; // Replace icons as needed
import FormatIcon from "@mui/icons-material/FormatPaint"; // Replace icons as needed

// Define a type for the HoverCard's props
interface HoverCardProps {
  title: string;
  hoverText: string;
  IconComponent: React.ElementType;
  onClick: () => void;
}

const useStyles = makeStyles({
  container: {
    padding: "20px",
    backgroundColor: "#f3f4f6",
    alignItems: "center",
  },
  leftSide: {
    backgroundColor: "transparent", // Dark background for the left section
    color: "black", // White text color
    padding: "20px",
    // minHeight: "100vh", // Ensure left side takes full height of the page
  },
  rightSide: {
    padding: "20px",
  },
  sectionTitle: {
    color: "#212D37", // Orange color for the section titles
    marginBottom: "10px",
  },
  paper: {
    position: "relative",
    padding: "16px",
    height: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    cursor: "pointer", // Cursor change to pointer for clickability
    "&:hover $hoverText": {
      transform: "translateY(0)",
      opacity: 1,
    },
    "&:active": {
      backgroundColor: "#ff6f00", // Color change when clicked
      transition: "background-color 0.3s ease",
    },
  },
  hoverText: {
    position: "absolute",
    bottom: "0",
    left: "0",
    right: "0",
    backgroundColor: "rgba(0,0,0,0.7)",
    color: "#fff",
    textAlign: "center",
    padding: "8px",
    transform: "translateY(100%)",
    transition: "transform 0.3s ease, opacity 0.3s ease",
    opacity: 0,
  },
  icon: {
    marginRight: "10px",
    color: "#ff6f00", // Orange icon color
  },
});

// Explicitly define the types for the props in HoverCard
const HoverCard: React.FC<HoverCardProps> = ({
  title,
  hoverText,
  IconComponent,
  onClick,
}) => {
  const classes = useStyles();

  return (
    <Paper elevation={3} className={classes.paper} onClick={onClick}>
      <IconComponent className={classes.icon} />
      <Typography variant="h6">{title}</Typography>
      <Box className={classes.hoverText}>
        <Typography variant="body2">{hoverText}</Typography>
      </Box>
    </Paper>
  );
};

const ServicesGrid = () => {
  const classes = useStyles();

  // Array of services with icons and hover text
  const services = [
    {
      title: "Topic and Research Proposal",
      hoverText: "Assistance in topic selection and proposal writing",
      icon: DescriptionIcon,
    },
    {
      title: "Problem Statement",
      hoverText: "We help define precise problem statements",
      icon: EditIcon,
    },
    {
      title: "Base Papers",
      hoverText: "Providing key base papers for your research",
      icon: ArticleIcon,
    },
    {
      title: "Chapters Writing",
      hoverText: "Support in writing different thesis chapters",
      icon: DescriptionIcon,
    },
    {
      title: "Questionnaire and Experiments",
      hoverText: "Designing questionnaires and experiments",
      icon: DescriptionIcon,
    },
    {
      title: "Implementation",
      hoverText: "Help in implementing research methodologies",
      icon: FormatIcon,
    },
    {
      title: "Analysis",
      hoverText: "Data analysis for research",
      icon: DescriptionIcon,
    },
    {
      title: "Editing",
      hoverText: "Professional Project writing services for your papers",
      icon: EditIcon,
    },
    {
      title: "Formatting",
      hoverText: "Formatting research papers according to guidelines",
      icon: FormatIcon,
    },
    {
      title: "Journal Papers",
      hoverText: "Help in writing journal papers",
      icon: ArticleIcon,
    },
    {
      title: "IEEE Papers",
      hoverText: "Writing and formatting IEEE papers",
      icon: DescriptionIcon,
    },
    {
      title: "Thought Clearing",
      hoverText: "Providing clarity and direction for your ideas",
      icon: DescriptionIcon,
    },
    // You can add more services as needed, following this structure
  ];

  // Explicitly define the type for the title in the click handler
  const handleCardClick = (title: string) => {
    // alert(`You clicked on: ${title}`); // This can be customized to navigate or trigger other actions
  };

  return (
    <Grid container className={classes.container}>
      {/* Left side - static content */}
      <Grid
        item
        xs={12}
        sm={4}
        className="{classes.leftSide} component text-black"
      >
        <Box mb={4}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Our Services
          </Typography>
          <Typography variant="body1">
            We include all the research related services for students to provide
            them a single stop for their research and research publication
            needs.
          </Typography>
        </Box>
        <Box mb={4}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Our Experts
          </Typography>
          <Typography variant="body1">
            To serve different services and one client at a time, we have a team
            of 790+ experts including subject specialists, writers, editors,
            analysts, statisticians, developers, and programmers.
          </Typography>
        </Box>
        <Box>
          <Typography variant="h5" className={classes.sectionTitle}>
            Our Guarantee
          </Typography>
          <Typography variant="body1">
            We guarantee quality and time preservation in our services.
            Moreover, we maintain an interactive service process with our
            clients to keep the service satisfactory.
          </Typography>
        </Box>
      </Grid>

      {/* Right side - service list */}
      <Grid item xs={12} sm={8} className={classes.rightSide}>
        <Grid container spacing={2}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} key={index} className="component">
              <HoverCard
                title={service.title}
                hoverText={service.hoverText}
                IconComponent={service.icon}
                onClick={() => handleCardClick(service.title)} // Handle click
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ServicesGrid;
